import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import NavBarNB from '../../components/NavBarNB';
import FooterNB from '../../components/FooterNB';
import { supabase } from '../../lib/supabase';
import { getErrorMessage } from '../../lib/errors';
import { useAuthStore } from '../../stores/authStore';
import ButtonNB from '../../components/ui-nb/ButtonNB';
import CardNB from '../../components/ui-nb/CardNB';
import InputNB from '../../components/ui-nb/InputNB';
import { debug, error as logError } from '../../lib/logger';

export default function CreateOrganizationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, signIn } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgName, setOrgName] = useState('');
  const [branchName, setBranchName] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!orgName.trim() || !branchName.trim()) {
      setError('Please fill in all fields');
      return;
    }

    if (!user?.id) {
      setError('Session expired. Please login again.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Verify Supabase session is active — Zustand state may be stale from localStorage
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Session expired. Please login again.');
        setLoading(false);
        return;
      }
      debug('[CreateOrg] Session valid', { userId: session.user.id });
      // Generate slug from org name
      const slug = orgName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
        .substring(0, 50);

      // Use SECURITY DEFINER RPC — bypasses RLS, handles missing profiles
      const { data, error: rpcError } = await supabase
        .rpc('create_organization_with_branch', {
          p_org_name: orgName.trim(),
          p_org_slug: `${slug}-${Date.now()}`,
          p_branch_name: branchName.trim(),
          p_user_id: session.user.id,
        });

      if (rpcError) throw rpcError;
      if (!data || data.length === 0) throw new Error('Organization creation returned no data');

      const result = data[0];

      // Update auth store
      signIn({
        ...user,
        organizationId: result.org_id,
        organizationName: result.org_name,
        role: 'admin',
      });

      // Redirect to admin dashboard
      navigate('/en/admin');
    } catch (err) {
      logError('Create org error', { error: getErrorMessage(err) });
      setError(getErrorMessage(err) || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-nb-cream">
      <NavBarNB />
      <main className="flex-1 max-w-md mx-auto px-4 py-16">
        <h1
          className="text-3xl font-bold text-black font-nb mb-2">
          {t('setup.createOrg.title') || 'Create Your Business'}
        </h1>
        <p className="mb-8 text-nb-text-secondary">
          {t('setup.createOrg.subtitle') || 'Set up your first location'}
        </p>

        {error && (
          <CardNB className="mb-6 border-2 border-nb-red">
            <p className="text-nb-red text-sm">{error}</p>
          </CardNB>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <InputNB
            label={t('setup.createOrg.orgName') || 'Business Name'}
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            placeholder={t('setup.createOrg.orgNamePlaceholder') || 'My Repair Shop'}
            disabled={loading}
          />

          <InputNB
            label={t('setup.createOrg.branchName') || 'First Location Name'}
            value={branchName}
            onChange={(e) => setBranchName(e.target.value)}
            placeholder={t('setup.createOrg.branchNamePlaceholder') || 'Main Store'}
            disabled={loading}
          />

          <ButtonNB variant="primary" type="submit" loading={loading} className="w-full">
            {loading
              ? (t('common.loading') || 'Loading...')
              : (t('setup.createOrg.submit') || 'Create Business')}
          </ButtonNB>
        </form>

        <p className="mt-6 text-center text-sm text-nb-text-muted">
          <button
            onClick={() => navigate('/en/setup/role')}
            className="text-nb-black hover:underline">
            {t('common.back') || 'Go back'}
          </button>
        </p>
      </main>
      <FooterNB />
    </div>
  );
}
